import { appPushNotificationManager } from "@common/core/capacitor/app-push-notifications-manager";
import { AppPushPermissionStatus } from "@common/core/capacitor/app-push-notifications/assets/app-push-notifications-interface";
import { Button } from "@ui/buttons/button";
import { Trans } from "@ui/i18n/trans";
import { NotificationsActiveIcon } from "@ui/icons/material/NotificationsActive";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useAuth } from "@common/auth/use-auth";

interface NotificationActivationPanelProps {
  messageText?: string;
  buttonText?: string;
  className?: string;
  displayInChannel?: boolean;
  onPermissionGranted?: () => void;
  onPermissionDenied?: () => void;
}

interface NotificationPermissionResolution {
  hasPermission: boolean | null;
  isResolved: boolean;
  permissionStatus: AppPushPermissionStatus;
}

const RESOLVED_PERMISSION_STATUSES: AppPushPermissionStatus[] = [
  "granted",
  "denied",
  "default",
  "prompt",
  "prompt-with-rationale",
];

async function delay(ms: number): Promise<void> {
  await new Promise(resolve => setTimeout(resolve, ms));
}

async function resolveNotificationPermission(): Promise<NotificationPermissionResolution> {
  try {
    await appPushNotificationManager.loadManager();

    if (!appPushNotificationManager.isNotificationSupported()) {
      return {
        hasPermission: null,
        isResolved: true,
        permissionStatus: "unsupported",
      };
    }

    for (let attempt = 0; attempt < 4; attempt++) {
      const permissionStatus = await appPushNotificationManager.getPermissionStatus();

      if (permissionStatus === "granted") {
        return {
          hasPermission: true,
          isResolved: true,
          permissionStatus,
        };
      }

      if (RESOLVED_PERMISSION_STATUSES.includes(permissionStatus)) {
        return {
          hasPermission: false,
          isResolved: true,
          permissionStatus,
        };
      }

      await delay(150);
    }

    return {
      hasPermission: null,
      isResolved: false,
      permissionStatus: "unknown",
    };
  } catch {
    return {
      hasPermission: false,
      isResolved: true,
      permissionStatus: "unknown",
    };
  }
}

export function NotificationActivationPanel({ 
  className,
  messageText = "Enable push notifications to stay up to date with your favorite artists and creators.",
  buttonText = "Enable push notifications",
  displayInChannel = false,
  onPermissionGranted,
  onPermissionDenied
}: NotificationActivationPanelProps) {
  const { isLoggedIn } = useAuth();
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isResolvingPermission, setIsResolvingPermission] = useState(true);
  const [permissionStatus, setPermissionStatus] =
    useState<AppPushPermissionStatus>("unknown");
  
  useEffect(() => {
    if (!isLoggedIn) {
      setHasPermission(null);
      setIsResolvingPermission(false);
      setPermissionStatus("unknown");
      return;
    }

    let cancelled = false;

    const syncPermission = async () => {
      setIsResolvingPermission(true);
      const permissionState = await resolveNotificationPermission();

      if (!cancelled) {
        setHasPermission(permissionState.hasPermission);
        setIsResolvingPermission(!permissionState.isResolved);
        setPermissionStatus(permissionState.permissionStatus);
      }
    };

    void syncPermission();

    const handleFocus = () => {
      void syncPermission();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void syncPermission();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isLoggedIn]);

  if (!isLoggedIn) {
    return null;
  }

  if (isResolvingPermission || hasPermission === null) {
    return null; // ou un loader léger si tu veux
  }

  if (hasPermission) {
    return null; // ne rien afficher si déjà activé
  }

  if (permissionStatus === "denied") {
    return null;
  }

  const handleEnable = async () => {
    setIsResolvingPermission(true);
    await appPushNotificationManager.loadManager();
    await appPushNotificationManager.openConfig();
    const permissionState = await resolveNotificationPermission();
    setHasPermission(permissionState.hasPermission);
    setIsResolvingPermission(!permissionState.isResolved);
    setPermissionStatus(permissionState.permissionStatus);
    
    if (permissionState.hasPermission && onPermissionGranted) {
      onPermissionGranted();
    } else if (permissionState.hasPermission === false && onPermissionDenied) {
      onPermissionDenied();
    }
  };
  
  return (
    <div className={clsx(
      "flex flex-col",
      displayInChannel
        ? "rounded-panel px-8 pt-16 pb-8 md-p16 items-center"
        : "gap-14 px-32 py-20 bg-alt border-b items-end items-end",
      className
    )}>
      <div className={clsx(
        "flex items-center gap-16",
        displayInChannel ? "flex-col" : ""
      )}>
        <div className={clsx(
          "rounded-full bg-primary",
          displayInChannel ? "p-10" : "p-5"
        )}><NotificationsActiveIcon color="#fff" size={displayInChannel ? "lg" : "sm"} /></div>
        <Trans message={messageText} />
      </div>
      <Trans
          message={`<a>${buttonText}</a>`}
          values={{
          a: text => (
              <Button
                elementType={"button"}
                variant="text"
                color={"primary"}
                display=""
                className="mt-10 max-w-max font-semibold"
                onClick={handleEnable}
              >
                {text}
              </Button>
          ),
          }}
      />
    </div>
  );
}
